import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { retentionSteps } from '@/components/docs/retentionStepsData';
import { DocStepCard } from '@/components/docs/DocStepCard';
import { useNavigate } from 'react-router-dom';

function LifecycleFlow() {
  return (
    <Card className="bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/20 dark:to-violet-950/20 border-blue-200 dark:border-blue-800">
      <CardContent className="py-5">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Payout Lifecycle</p>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="px-3 py-1.5 rounded-full font-medium border bg-blue-100 text-blue-700 border-blue-300">Select Period</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 rounded-full font-medium border bg-orange-100 text-orange-700 border-orange-300">Generate</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 rounded-full font-medium border bg-amber-100 text-amber-700 border-amber-300">Review & Edit</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 rounded-full font-medium border bg-indigo-100 text-indigo-700 border-indigo-300">Finalize</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 rounded-full font-medium border bg-emerald-100 text-emerald-700 border-emerald-300">Mark Paid</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 rounded-full font-medium border bg-gray-200 text-gray-700 border-gray-400">Export</span>
        </div>
      </CardContent>
    </Card>
  );
}

function generatePDF() {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    let y = 20;
    const check = (n) => { if (y + n > 270) { doc.addPage(); y = 20; } };

    doc.setFontSize(22);
    doc.setTextColor(59, 130, 246);
    doc.text('Retention Bonus Payout', 20, y);
    y += 10;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('Process Documentation — PayTrack', 20, y);
    y += 6;
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(20, y, pw - 20, y);
    y += 12;

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Payout Lifecycle:', 20, y);
    y += 7;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.text('Select Period > Generate > Review & Edit > Finalize > Mark Paid > Export', 20, y);
    y += 14;

    for (let i = 0; i < retentionSteps.length; i++) {
      const step = retentionSteps[i];
      check(50);
      doc.setFontSize(13);
      doc.setTextColor(59, 130, 246);
      doc.setFont(undefined, 'bold');
      doc.text((i + 1) + '. ' + step.title, 20, y);
      y += 3;
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text('Page: ' + step.page, 20, y + 3);
      y += 9;
      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);
      doc.setFont(undefined, 'normal');
      for (let j = 0; j < step.content.length; j++) {
        check(8);
        const lines = doc.splitTextToSize('- ' + step.content[j], pw - 45);
        doc.text(lines, 24, y);
        y += lines.length * 5;
      }
      y += 2;
      if (step.details) {
        for (let j = 0; j < step.details.length; j++) {
          check(12);
          doc.setFont(undefined, 'bold');
          doc.text(step.details[j].label + ':', 28, y);
          doc.setFont(undefined, 'normal');
          const dl = doc.splitTextToSize(step.details[j].desc, pw - 75);
          doc.text(dl, 65, y);
          y += Math.max(dl.length * 5, 6);
        }
      }
      if (step.note) {
        check(12);
        doc.setFontSize(9);
        doc.setTextColor(30, 80, 160);
        const nl = doc.splitTextToSize('Note: ' + step.note, pw - 50);
        doc.text(nl, 24, y);
        y += nl.length * 4.5 + 2;
      }
      if (step.important) {
        check(12);
        doc.setFontSize(9);
        doc.setTextColor(180, 80, 0);
        const il = doc.splitTextToSize('Important: ' + step.important, pw - 50);
        doc.text(il, 24, y);
        y += il.length * 4.5 + 2;
      }
      y += 8;
      doc.setTextColor(40, 40, 40);
    }

    check(30);
    doc.setFontSize(12);
    doc.setTextColor(59, 130, 246);
    doc.setFont(undefined, 'bold');
    doc.text('Role Permissions:', 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.setFont(undefined, 'normal');
    doc.text('Super Admin: Generate, Edit, Finalize, Pay, Revert, Export', 20, y); y += 6;
    doc.text('Admin: Generate, Edit, Finalize, Pay, Export', 20, y); y += 6;
    doc.text('Payroll Auditor / Account Executive: View, Export', 20, y);

    doc.save('Retention_Bonus_Payout_Guide.pdf');
  });
}

function RoleTable() {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex gap-2">
        <span className="font-semibold min-w-[180px]">Super Admin:</span>
        <span className="text-muted-foreground">Generate, Edit Amounts, Finalize, Mark Paid, Revert, Export</span>
      </div>
      <div className="flex gap-2">
        <span className="font-semibold min-w-[180px]">Admin:</span>
        <span className="text-muted-foreground">Generate, Edit Amounts, Finalize, Mark Paid, Export</span>
      </div>
      <div className="flex gap-2">
        <span className="font-semibold min-w-[180px]">Payroll Auditor:</span>
        <span className="text-muted-foreground">View payouts, Export</span>
      </div>
      <div className="flex gap-2">
        <span className="font-semibold min-w-[180px]">Account Executive:</span>
        <span className="text-muted-foreground">View payouts, Mark Paid, Export</span>
      </div>
    </div>
  );
}

function StepsList() {
  const cards = [];
  for (let i = 0; i < retentionSteps.length; i++) {
    cards.push(<DocStepCard key={retentionSteps[i].id} step={retentionSteps[i]} index={i} />);
  }
  return <div className="space-y-3">{cards}</div>;
}

export default function RetentionBonusDocsPage() {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="retention-docs-page">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/docs')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2 cursor-pointer"
            data-testid="back-to-docs-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Docs
          </button>
          <h1 className="text-2xl font-bold tracking-tight">Retention Bonus Payout</h1>
          <p className="text-muted-foreground mt-1">How to generate, review, finalize, and pay half-yearly retention bonuses</p>
        </div>
        <Button onClick={generatePDF} variant="outline" className="gap-2" data-testid="download-pdf-btn">
          <FileDown className="h-4 w-4" />
          Download PDF
        </Button>
      </div>

      <LifecycleFlow />

      <Card className="border-dashed">
        <CardContent className="py-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Payout Formula</p>
          <p className="font-mono text-sm">
            <span className="text-emerald-600 font-semibold">Total Payout</span> = Monthly Retention
            <span className="text-blue-600"> x Eligible Months</span>
            <span className="text-muted-foreground"> (editable override available)</span>
          </p>
        </CardContent>
      </Card>

      <StepsList />

      <Card>
        <CardContent className="py-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Role Permissions</p>
          <RoleTable />
        </CardContent>
      </Card>
    </div>
  );
}
